/* eslint-disable react/prop-types */
import { Link } from "react-router-dom";
import PhaseChip from "../../../components/PhaseChip";
import ContactActions from "../../../components/ContactActions";
import ConvertFromButton from "../../../components/ConvertFromButton";
import SearchableSelect from "../../../components/SearchableSelect";
import { mailtoUrl } from "../../../lib/contact";
import VoiceTextarea from "../../../components/VoiceTextarea";
import MultiLinkChipRow from "../../../components/MultiLinkChipRow";
import { TICKET_STATUSES, TICKET_SUPPORT_TYPES } from "../../../lib/enums";

/**
 * TicketOverviewSection — Phase 7 Sub-iter 7A.
 *
 * The Overview tab body for the Ticket detail page.  Holds subject,
 * description, status / assignee / support type pickers, and the
 * "Convert to job card" action.  Edit mode shares the same form
 * (DESIGN_CONTRACT §1 — single editor per entity).
 */
const CLOSED_STATUSES = new Set(["resolved_via_jc", "resolved_external", "cancelled"]);

// Iter 430.t1 — ISO → `YYYY-MM-DDTHH:MM` for <input type="datetime-local">.
function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

/**
 * TicketLifecycleStrip — read-only timestamp row.
 * Shows the key milestones captured by the backend audit trail so staff
 * can see the full lifecycle at a glance without opening the Activity tab.
 */
function TicketLifecycleStrip({ t }) {
  const items = [
    { label: "Logged",   at: t.created_at,  by: t.created_by_name },
    { label: "Updated",  at: t.updated_at,  by: t.updated_by_name },
    { label: "Resolved", at: t.resolved_at, by: t.resolved_by_name },
    { label: "Closed",   at: t.closed_at,   by: t.closed_by_name },
    { label: "Reopened", at: t.reopened_at, by: t.reopened_by_name },
  ].filter((x) => x.at);
  if (!items.length) return null;
  return (
    <div
      data-testid="ticket-lifecycle-strip"
      className="mt-3 flex flex-wrap gap-x-5 gap-y-2 border-y border-white/5 py-2 text-[11px] text-[#a0aabf]"
    >
      {items.map((it) => (
        <div key={it.label} className="min-w-0">
          <span className="uppercase tracking-wider text-[#647087]">{it.label}:</span>{" "}
          <span className="text-white/90">{fmtDT(it.at)}</span>
          {it.by && <span className="text-[#647087]"> · by {it.by}</span>}
        </div>
      ))}
    </div>
  );
}

export default function TicketOverviewSection({
  t,
  customer,
  customerSites = [],
  allCustomers = [],
  editMode,
  editForm,
  setEditForm,
  isManager,
  staff,
  onUpdateStatus,
  onAssign,
  onConvert,
  onLinksChanged,
}) {
  // Iter 418.ba (Dirk, 2026-02-28) — Edit mode exposes every field the
  // create form does (site, priority, assignee) PLUS the audit-only
  // fields staff need to fix mid-life (external_ref, resolution_notes,
  // time_logged, hidden_from_client). Read-only view stays lean.
  const priorityLabel = {
    low: "Low", normal: "Normal", high: "High", urgent: "Urgent",
  }[t.priority] || (t.priority || "—");
  const siteLabel = customerSites.find((s) => s.id === t.site_id)?.name
    || customerSites.find((s) => s.id === t.site_id)?.title
    || (t.site_id ? "Linked site" : "—");
  return (
    <div className="hv-card p-5 md:p-6 space-y-3" data-testid="ticket-overview-section">
      <PhaseChip value={t.status} />
      {editMode ? (
        <input
          value={editForm.subject}
          onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
          className="hv-input font-display text-lg md:text-2xl font-bold"
          data-testid="ticket-input-subject"
        />
      ) : (
        <h1 className="font-display text-xl md:text-3xl font-bold tracking-tight mt-2 flex items-center gap-3 break-words">
          {t.short_id && (
            <span className="text-base font-mono text-rose-300/80">#{t.short_id}</span>
          )}
          {t.subject}
        </h1>
      )}
      {customer && !editMode && (
        <Link
          to={`/customers/${customer.id}`}
          className="text-sm text-[#1fb6c1] hover:underline inline-block"
        >
          {customer.name}
          {customer.external_ref && (
            <span className="font-mono text-xs text-[#647087]"> · {customer.external_ref}</span>
          )}
        </Link>
      )}
      {/* Iter 430.t1 — Customer picker in edit mode. Dirk: "if a support
          ticket is now assigned to the wrong customer, it's impossible
          to move it". Lets staff relocate the ticket to the correct
          customer; clearing the site_id so a stale site reference
          doesn't follow. */}
      {editMode && (
        <div data-testid="ticket-edit-customer-wrap" className="mt-2">
          <label className="text-xs uppercase tracking-wider text-[#647087]">Customer</label>
          <SearchableSelect
            value={editForm.customer_id || ""}
            onChange={(cid) =>
              setEditForm({ ...editForm, customer_id: cid, site_id: "" })
            }
            options={(() => {
              const list = (allCustomers || []).map((c) => ({
                id: c.id,
                label: c.name || "(unnamed)",
                sublabel: c.external_ref || c.email || c.phone || undefined,
                searchTerms: [c.external_ref, c.email, c.phone, c.vat_number].filter(Boolean),
              }));
              // Guarantee the currently-selected customer is always in
              // the options so the SearchableSelect shows its label
              // even before the full tenant list has loaded.
              if (customer && !list.some((o) => o.id === customer.id)) {
                list.unshift({
                  id: customer.id,
                  label: customer.name || "(current)",
                  sublabel: customer.external_ref || customer.email || customer.phone || undefined,
                });
              }
              return list;
            })()}
            placeholder="— pick customer —"
            testId="ticket-input-customer"
            emptyHint="Type to find a customer by name, phone, email, VAT…"
          />
        </div>
      )}
      {customer && !editMode && (customer.phone || customer.email) && (
        <div className="mt-2" data-testid="ticket-customer-contact">
          <ContactActions
            phone={customer.phone || customer.whatsapp}
            email={customer.email}
            name={customer.name}
            org={customer.name}
            showVCard={false}
            compact={false}
            mobileOnlyExtras
            testIdPrefix="ticket-contact"
          />
          {customer.email && (
            <a
              href={mailtoUrl({ to: customer.email, subject: `Re: ${t.subject || ""}` })}
              className="hv-btn hv-btn-ghost text-xs ml-1.5 inline-flex items-center gap-1 mt-1"
              data-testid="ticket-contact-email"
            >
              ✉ Email
            </a>
          )}
        </div>
      )}
      {/* Iter 430.t1 — lifecycle timestamp strip. Dirk: "support tickets
          logs time as well, timestamps, everything, when it was created,
          when it was resolved, when it's moving through the different
          stages". Full breakdown is on the Activity tab. */}
      {!editMode && <TicketLifecycleStrip t={t} />}
      {editMode ? (
        <VoiceTextarea
          value={editForm.description}
          onChange={(v) => setEditForm({ ...editForm, description: v })}
          placeholder="Description — what's happening, when, urgency. Or tap the mic."
          rows={5}
          context="lead_activity"
          testIdPrefix="ticket-desc-voice"
          data-testid="ticket-input-description"
        />
      ) : t.description ? (
        <p className="text-sm text-[#a0aabf] mt-3 leading-relaxed whitespace-pre-wrap">
          {t.description}
        </p>
      ) : null}

      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#647087]">Status</label>
          {editMode || isManager ? (
            <select
              value={editMode ? editForm.status : t.status}
              onChange={(e) =>
                editMode
                  ? setEditForm({ ...editForm, status: e.target.value })
                  : onUpdateStatus(e.target.value)
              }
              className="hv-input mt-1"
              data-testid="ticket-status-select"
            >
              {TICKET_STATUSES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          ) : (
            <div className="hv-input mt-1 opacity-70">{(t.status || "").replaceAll("_", " ")}</div>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#647087]">Priority</label>
          {editMode ? (
            <select
              value={editForm.priority || "normal"}
              onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
              className="hv-input mt-1"
              data-testid="ticket-input-priority"
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          ) : (
            <div className="hv-input mt-1 opacity-70">{priorityLabel}</div>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#647087]">Assigned to</label>
          {editMode ? (
            <select
              value={editForm.assigned_to_id || ""}
              onChange={(e) => setEditForm({ ...editForm, assigned_to_id: e.target.value })}
              className="hv-input mt-1"
              data-testid="ticket-input-assigned"
            >
              <option value="">— Unassigned —</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.email} {s.role ? `(${s.role})` : ""}
                </option>
              ))}
            </select>
          ) : isManager ? (
            <select
              value={t.assigned_to_id || ""}
              onChange={(e) => onAssign(e.target.value)}
              className="hv-input mt-1"
              data-testid="ticket-assign-select"
            >
              <option value="">— Unassigned —</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.email} {s.role ? `(${s.role})` : ""}
                </option>
              ))}
            </select>
          ) : (
            <div className="hv-input mt-1 opacity-70" data-testid="ticket-assignee-display">
              {t.assigned_to_id
                ? staff.find((s) => s.id === t.assigned_to_id)?.name || "Assigned"
                : "Unassigned"}
            </div>
          )}
          {!editMode && isManager && t.assigned_to_id && (
            <p className="text-[10px] text-[#647087] mt-1 leading-tight">
              Assigning alerts the team member and auto-creates a task linked to this ticket.
            </p>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Support type</label>
          {editMode ? (
            <select
              value={editForm.support_type}
              onChange={(e) => setEditForm({ ...editForm, support_type: e.target.value })}
              className="hv-input mt-1"
              data-testid="ticket-input-support-type"
            >
              <option value="">— Select —</option>
              {TICKET_SUPPORT_TYPES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          ) : (
            <div className="hv-input mt-1 opacity-70">
              {TICKET_SUPPORT_TYPES.find((s) => s.key === t.support_type)?.label ||
                t.support_type ||
                "—"}
            </div>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#647087]">Site</label>
          {editMode ? (
            <select
              value={editForm.site_id || ""}
              onChange={(e) => setEditForm({ ...editForm, site_id: e.target.value })}
              className="hv-input mt-1"
              data-testid="ticket-input-site"
            >
              <option value="">— No site —</option>
              {customerSites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.title || s.address || s.id.slice(0, 8)}
                </option>
              ))}
            </select>
          ) : (
            <div className="hv-input mt-1 opacity-70">{siteLabel}</div>
          )}
        </div>
        {/* Iter 430.t1 — editable incident date. Defaults to created_at
            on read; staff can correct it after the fact when a ticket
            was logged late. */}
        <div>
          <label className="text-xs uppercase tracking-wider text-[#647087]">Incident date</label>
          {editMode ? (
            <input
              type="datetime-local"
              value={toLocalInput(editForm.ticket_date)}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  ticket_date: e.target.value ? new Date(e.target.value).toISOString() : "",
                })
              }
              className="hv-input mt-1"
              data-testid="ticket-input-date"
            />
          ) : (
            <div className="hv-input mt-1 opacity-70" data-testid="ticket-date-readonly">
              {t.ticket_date
                ? new Date(t.ticket_date).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
                : (t.created_at
                    ? new Date(t.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
                    : "—")}
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <MultiLinkChipRow
            entityKind="ticket"
            entityId={t.id}
            customerId={t.customer_id}
            targetKind="jobcard"
            targetIds={[
              // Iter 418.bf — union of new many-to-many array + legacy singular
              ...(t.jobcard_ids || []),
              ...(t.linked_job_card_id && !(t.jobcard_ids || []).includes(t.linked_job_card_id)
                ? [t.linked_job_card_id] : []),
            ]}
            canEdit={editMode || isManager}
            label="Linked job cards"
            onChanged={onLinksChanged}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#647087]">External ref</label>
          {editMode ? (
            <input
              type="text"
              value={editForm.external_ref || ""}
              onChange={(e) => setEditForm({ ...editForm, external_ref: e.target.value })}
              placeholder="Customer PO / external ticket ID…"
              className="hv-input mt-1"
              data-testid="ticket-input-external-ref"
            />
          ) : (
            <div className="hv-input mt-1 opacity-70">{t.external_ref || "—"}</div>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#647087]">Time logged (min)</label>          {editMode ? (
            <input
              type="number"
              min="0"
              step="1"
              value={editForm.time_logged || ""}
              onChange={(e) => setEditForm({ ...editForm, time_logged: e.target.value ? Number(e.target.value) : "" })}
              placeholder="0"
              className="hv-input mt-1"
              data-testid="ticket-input-time-logged"
            />
          ) : (
            <div className="hv-input mt-1 opacity-70">{t.time_logged || "—"}</div>
          )}
        </div>
      </div>

      {editMode && (
        <>
          <div className="mt-3">
            <label className="text-xs uppercase tracking-wider text-[#647087]">Resolution notes</label>
            <VoiceTextarea
              value={editForm.resolution_notes || ""}
              onChange={(v) => setEditForm({ ...editForm, resolution_notes: v })}
              placeholder="What was done to resolve this — parts used, root cause, follow-ups…"
              rows={3}
              context="lead_activity"
              testIdPrefix="ticket-resolution-voice"
              data-testid="ticket-input-resolution-notes"
            />
          </div>
          <label className="mt-3 flex items-start gap-2 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!editForm.hidden_from_client}
              onChange={(e) => setEditForm({ ...editForm, hidden_from_client: e.target.checked })}
              className="mt-1 accent-[#22d3ee]"
              data-testid="ticket-input-hidden-from-client"
            />
            <span className="flex-1">
              <span className="font-semibold text-white">Hide from client portal</span>
              <span className="block text-[11px] text-[#a0aabf] mt-0.5">
                Internal-only ticket — the customer's portal will not list this.
              </span>
            </span>
          </label>
        </>
      )}

      {!CLOSED_STATUSES.has(t.status) && !editMode && (
        <div className="mt-3">
          <ConvertFromButton
            targets={["jobcard"]}
            onConvert={onConvert}
            testIdPrefix="ticket-convert"
          />
        </div>
      )}
    </div>
  );
}
